import { FieldAddonDropdown, InputText } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type {
  SearchDuration,
  SearchDurationUnit,
  SearchFilterConditionDuration,
  SearchFilterValueString,
} from 'lib-shared-types-api';
import isNil from 'lodash/isNil';
import { ChangeEvent } from 'react';
import { useFirstMountState, useMap, useShallowCompareEffect } from 'react-use';

import {
  convertDurationToMs,
  convertMsToDuration,
} from 'app/views/layout/views/search/utils/duration/convertDuration';

import { ConditionFieldProps } from '../../conditionField';

import styles from './conditionFieldDuration.module.scss';

type DurationUnitItem = {
  id: SearchDurationUnit;
  name: SearchDurationUnit;
  disabled?: boolean;
};

type DurationState = {
  minDuration: SearchDuration;
  maxDuration: SearchDuration;
};

const getDurationState = (
  condition: SearchFilterConditionDuration,
): DurationState => {
  const [min, max] = condition?.values ?? [];

  const minDuration = convertMsToDuration(parseInt(min?.value ?? '0'));
  const maxDuration = convertMsToDuration(parseInt(max?.value ?? '0'));

  return { minDuration, maxDuration };
};

export const ConditionFieldDuration = ({
  condition,
  onChange,
  onError,
}: ConditionFieldProps<SearchFilterConditionDuration>): JSX.Element => {
  const isRangeDuration = condition.operator === 'between';

  const [{ minDuration, maxDuration }, durationSetters] = useMap<DurationState>(
    getDurationState(condition),
  );

  const isFirstMount = useFirstMountState();

  // Updates the internal duration state when the condition changes from outside the component.
  useShallowCompareEffect(() => {
    if (!isFirstMount) {
      durationSetters.setAll(getDurationState(condition));
    }
  }, [condition]);

  // Updates the condition values when the internal duration state changes and is valid.
  useShallowCompareEffect(() => {
    if (isFirstMount) return;

    const isValid = validateDurationInputs();
    if (!isValid) return;

    const values: SearchFilterValueString[] = [];

    if (isRangeDuration) {
      values.push(
        {
          label: `${minDuration.value} ${minDuration.unit.toLowerCase()}`,
          value: convertDurationToMs(
            minDuration.value,
            minDuration.unit,
          ).toString(),
        },
        {
          label: `${maxDuration.value} ${maxDuration.unit.toLowerCase()}`,
          value: convertDurationToMs(
            maxDuration.value,
            maxDuration.unit,
          ).toString(),
        },
      );
    } else if (minDuration.value) {
      values.push({
        label: `${minDuration.value} ${minDuration.unit.toLowerCase()}`,
        value: convertDurationToMs(
          minDuration.value,
          minDuration.unit,
        ).toString(),
      });
    }

    if (values.length) {
      onChange(values);

      ampli.searchFilterValueAdded({
        type: condition.field,
        operator: condition.operator,
        value: [
          minDuration.unit.toLowerCase(),
          ...(isRangeDuration ? [maxDuration.unit.toLowerCase()] : []),
        ].join('/'),
      });
    }
  }, [minDuration, maxDuration]);

  const handleChangeMinDuration = (e: ChangeEvent<HTMLInputElement>): void => {
    durationSetters.set('minDuration', {
      ...minDuration,
      value: parseInt(e.target.value),
    });
  };

  const handleChangeMaxDuration = (e: ChangeEvent<HTMLInputElement>): void => {
    durationSetters.set('maxDuration', {
      ...maxDuration,
      value: parseInt(e.target.value),
    });
  };

  const validateDurationInputs = (): boolean => {
    let errorMessage = '';

    if (isRangeDuration) {
      if (
        (!minDuration.value && minDuration.value !== 0) ||
        !maxDuration.value
      ) {
        errorMessage = 'Both max and min duration should be filled';
      } else {
        const minDurationInMs = convertDurationToMs(
          minDuration.value,
          minDuration.unit,
        );

        const maxDurationInMs = convertDurationToMs(
          maxDuration.value,
          maxDuration.unit,
        );

        if (minDurationInMs >= maxDurationInMs) {
          errorMessage = 'Max value should be bigger than min value';
        } else if (
          (minDurationInMs && minDurationInMs < 0) ||
          (maxDurationInMs && maxDurationInMs < 0)
        ) {
          errorMessage = 'Both values should be positive';
        }
      }
    } else {
      if (isNil(minDuration.value)) {
        errorMessage = 'The duration value should be filled';
      } else if (minDuration.value < 0) {
        errorMessage = 'The duration value should be positive';
      }
    }

    onError?.(errorMessage);
    return !errorMessage;
  };

  const handleSelectMinDurationUnit = (unit: SearchDurationUnit): void => {
    let nextMinDurationUnit = minDuration.unit;
    let nextMaxDurationUnit = maxDuration.unit;

    // if selected min duration unit is seconds,
    // update both min and max units to be seconds in the UI
    if (unit === 'seconds') {
      nextMinDurationUnit = unit;
      nextMaxDurationUnit = unit;
      // if selected min duration unit is minutes
      // and current selected max unit in the UI is seconds,
      // update both min and max units to be minutes in the UI
    } else if (unit === 'minutes' && maxDuration.unit === 'seconds') {
      nextMinDurationUnit = unit;
      nextMaxDurationUnit = unit;
      // if selected min duration unit is minutes
      // and current selected max unit in the UI is hours,
      // only update min duration unit to be minutes in the UI
    } else if (unit === 'minutes' && maxDuration.unit === 'hours') {
      nextMinDurationUnit = unit;
      // if selected min duration unit is hours
      // update both min and max units to be hours in the UI
    } else if (unit === 'hours') {
      nextMinDurationUnit = unit;
      nextMaxDurationUnit = unit;
    }

    durationSetters.setAll({
      minDuration: { ...minDuration, unit: nextMinDurationUnit },
      maxDuration: { ...maxDuration, unit: nextMaxDurationUnit },
    });
  };

  const handleSelectMaxDurationUnit = (unit: SearchDurationUnit): void => {
    durationSetters.set('maxDuration', {
      ...maxDuration,
      unit,
    });
  };

  const items: DurationUnitItem[] = [
    {
      id: 'seconds',
      name: 'seconds',
      disabled: ['minutes', 'hours'].includes(minDuration.unit),
    },
    {
      id: 'minutes',
      name: 'minutes',
      disabled: minDuration.unit === 'hours',
    },
    {
      id: 'hours',
      name: 'hours',
      disabled: false,
    },
  ];
  const disabledItems = items
    .filter((item) => item.disabled)
    .map((item) => item.id);

  const shorthand = {
    ['seconds']: 'sec',
    ['minutes']: 'min',
    ['hours']: 'hr',
  };

  return (
    <div className={styles.durationInputs}>
      <InputText
        type="number"
        name="min-duration"
        onChange={handleChangeMinDuration}
        value={minDuration.value}
        className={styles.durationInput}
        placeholder="Min"
        suffix={{
          type: 'custom',
          noWrap: true,
          value: (
            <FieldAddonDropdown
              meta={{
                selectedValue: shorthand[minDuration.unit],
              }}
              items={items}
              onSelect={(durationUnit) =>
                handleSelectMinDurationUnit(durationUnit.id)
              }
            />
          ),
        }}
        autoComplete="off"
        hideNumberArrows
      />

      {isRangeDuration && (
        <InputText
          type="number"
          name="max-duration"
          onChange={handleChangeMaxDuration}
          value={maxDuration.value}
          className={styles.durationInput}
          placeholder="Max"
          suffix={{
            type: 'custom',
            noWrap: true,
            value: (
              <FieldAddonDropdown
                meta={{
                  selectedValue: shorthand[maxDuration.unit],
                }}
                items={items}
                disabledItems={disabledItems}
                onSelect={(durationUnit) =>
                  handleSelectMaxDurationUnit(durationUnit.id)
                }
              />
            ),
          }}
          autoComplete="off"
          hideNumberArrows
        />
      )}
    </div>
  );
};
